import { useEffect, useState } from 'react';
import productService from '../../services/productService';
import categoryService from '../../services/categoryService';
import brandService from '../../services/brandService';
import { AdvertisementActionType } from './adConstants';

const SERVICE_BY_ACTION_TYPE = {
  [AdvertisementActionType.Product]: productService,
  [AdvertisementActionType.Category]: categoryService,
  [AdvertisementActionType.Brand]: brandService,
};

const label = (item, isRtl) => (isRtl ? (item.nameAr || item.nameEn) : (item.nameEn || item.nameAr)) || '';

/** Small searchable combobox fed by the existing Products/Categories/Brands admin list endpoints. */
const TargetPicker = ({ actionType, value, onChange, isRtl }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const service = SERVICE_BY_ACTION_TYPE[actionType];
    if (!service) {
      setItems([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    service
      .getAll()
      .then((data) => {
        if (cancelled) return;
        setItems(Array.isArray(data) ? data : (data?.items ?? []));
      })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [actionType]);

  const selected = items.find((item) => item.id === value);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? items.filter((item) => (item.nameEn || '').toLowerCase().includes(q) || (item.nameAr || '').toLowerCase().includes(q))
    : items;

  return (
    <div className="target-picker">
      {selected ? (
        <div className="target-picker-selected">
          <span className="target-picker-selected-name">{label(selected, isRtl)}</span>
          <button
            type="button"
            className="target-picker-clear"
            onClick={() => { onChange(null); setQuery(''); setOpen(true); }}
          >
            {isRtl ? 'تغيير' : 'Change'}
          </button>
        </div>
      ) : (
        <>
          <input
            type="text"
            className="form-input"
            placeholder={loading ? (isRtl ? 'جاري التحميل...' : 'Loading...') : (isRtl ? 'ابحث للاختيار...' : 'Search to select...')}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 200)}
            autoComplete="off"
          />
          {open && (
            <div className="target-picker-dropdown">
              {filtered.length === 0 ? (
                <div className="target-picker-empty">{isRtl ? 'لا توجد نتائج' : 'No results'}</div>
              ) : (
                filtered.slice(0, 50).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="target-picker-option"
                    onMouseDown={(e) => { e.preventDefault(); onChange(item.id); setQuery(''); setOpen(false); }}
                  >
                    {label(item, isRtl)}
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
      <style>{`
        .target-picker { position: relative; }
        .target-picker-selected {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--sp-2);
          padding: var(--sp-3) var(--sp-4);
          background: var(--neutral-50);
          border: 1px solid var(--border);
          border-radius: var(--r-md);
          min-height: 2.5rem;
        }
        .target-picker-selected-name { font-weight: 500; color: var(--text); }
        .target-picker-clear {
          padding: 0.25rem 0.6rem;
          font-size: var(--fs-xs);
          font-weight: 500;
          color: var(--primary);
          background: transparent;
          border: 1px solid var(--primary);
          border-radius: var(--r-sm);
          cursor: pointer;
          flex-shrink: 0;
        }
        .target-picker-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          margin-top: 2px;
          max-height: 220px;
          overflow-y: auto;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--r-md);
          box-shadow: var(--shadow-md);
          z-index: 50;
        }
        .target-picker-option {
          display: block;
          width: 100%;
          padding: var(--sp-3) var(--sp-4);
          text-align: inherit;
          font-size: var(--fs-sm);
          color: var(--text);
          background: none;
          border: none;
          cursor: pointer;
        }
        .target-picker-option:hover { background: var(--neutral-100); }
        .target-picker-empty {
          padding: var(--sp-4);
          font-size: var(--fs-sm);
          color: var(--text-muted);
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default TargetPicker;
